import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiLoader } from 'react-icons/fi';
import { apiService } from '../../components/Config/api.service';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';
import { BOUTON_PRINCIPAL, BOUTON_SECONDAIRE, enveloppeDe, stylesRecherche } from './organigrammeOutils';
import SansComptePortail from './SansComptePortail';

/**
 * Désigner ou retirer le responsable d'un département (Yros/Kavero#469, T2,
 * contrat C28 en commentaire du ticket).
 *
 * Deux temps, chacun derrière un bouton : choisir puis « Continuer », qui
 * demande l'aperçu ; lire l'aperçu puis « Confirmer », qui écrit. Odoo propage
 * le responsable : ceux qui suivaient l'ancien, ou n'en avaient pas, le
 * reçoivent ; ceux qui ont un responsable choisi à la main le gardent.
 */

function ResponsableDepartement({ departement, onDesigne, onFermer }) {
  const { t } = useTranslation();
  const [choix, setChoix] = useState(null);
  // `cible` : ce qui a été soumis à l'aperçu — `null` pour un retrait.
  const [cible, setCible] = useState(undefined);
  const [apercu, setApercu] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  const corps = (responsableId) => ({ departement_id: departement.id, responsable_id: responsableId });

  const prevoir = async (responsableId) => {
    setErreur('');
    setEnCours(true);
    const reponse = await enveloppeDe(() => apiService.prevoirResponsableDepartement(corps(responsableId)));
    setEnCours(false);
    if (reponse.ok) {
      setCible(responsableId);
      setApercu(reponse);
    } else {
      setErreur(reponse.message || t('organigramme.erreurGenerique'));
    }
  };

  const confirmer = async () => {
    setErreur('');
    setEnCours(true);
    const reponse = await enveloppeDe(() => apiService.designerResponsableDepartement(corps(cible)));
    setEnCours(false);
    if (reponse.ok) {
      onDesigne(reponse.departement);
    } else {
      setErreur(reponse.message || t('organigramme.erreurGenerique'));
    }
  };

  const responsable = apercu?.responsable;

  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
      {!apercu ? (
        <>
          <EmployeeSelect value={choix} onChange={setChoix} placeholder={t('organigramme.chercherResponsable')}
            styles={stylesRecherche} />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" className={BOUTON_PRINCIPAL} disabled={!choix || enCours}
              onClick={() => prevoir(choix.value)}>
              {enCours && <FiLoader className="animate-spin" />} {t('organigramme.continuer')}
            </button>
            {departement.responsable && (
              <button type="button" className="px-3 py-2 text-sm rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors duration-200" disabled={enCours}
                onClick={() => prevoir(null)}>
                {t('organigramme.retirer')}
              </button>
            )}
            <button type="button" className={`${BOUTON_SECONDAIRE} ml-auto`} onClick={onFermer}>
              {t('organigramme.annuler')}
            </button>
          </div>
        </>
      ) : (
        <>
          {responsable ? (
            <p className="text-gray-800">
              {t('organigramme.apercuRecoivent', { n: apercu.recoivent, nom: responsable.nom })}
            </p>
          ) : (
            <p className="text-gray-800">{t('organigramme.apercuRetrait', { n: apercu.recoivent })}</p>
          )}
          {apercu.gardent > 0 && (
            <p className="mt-1 text-gray-600">{t('organigramme.apercuGardent', { n: apercu.gardent })}</p>
          )}
          {responsable && !responsable.compte_portail && <SansComptePortail personne={responsable} />}
          <div className="mt-3 flex items-center gap-2">
            <button type="button" className={BOUTON_PRINCIPAL} disabled={enCours} onClick={confirmer}>
              {enCours && <FiLoader className="animate-spin" />} {t('organigramme.confirmer')}
            </button>
            <button type="button" className={BOUTON_SECONDAIRE} disabled={enCours}
              onClick={() => { setApercu(null); setErreur(''); }}>
              {t('organigramme.retour')}
            </button>
          </div>
        </>
      )}
      {erreur && (
        <p role="alert" className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-red-700">{erreur}</p>
      )}
    </div>
  );
}

export default ResponsableDepartement;
